import { zodResolver } from '@hookform/resolvers/zod';
import { Save, Delete as Trash2 } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';
import { FieldErrors, FormProvider, Path, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { FloatingSaveButton } from '@/components/floating-save-button';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { BasicIdentitySection } from '@/components/organization-form/basic-identity';
import { CustomFieldsSection } from '@/components/organization-form/custom-fields-section';
import { ImageManager } from '@/components/organization-form/image-manager';
import { MembershipSection } from '@/components/organization-form/membership-section';
import { Page } from '@/components/page';
import {
  OrganizationFormSchema,
  OrganizationFormValues,
  useOrganizationAccessLayer,
} from '@/features/organizations';
import {
  getChildOrganizationIds,
  getOrganizationMemberIds,
  getParentOrganizationIds,
  removeOrganizationFromCharacters,
  removeOrganizationFromOrganizations,
  syncCharactersForOrganization,
  syncChildOrganizationsForOrganization,
  syncParentOrganizationsForOrganization,
} from '@/features/organizations/utils/membership-sync';
import {
  ORGANIZATION_HIERARCHY_FEATURE_KEY,
  stripOrganizationHierarchyFields,
} from '@/features/organizations/utils/organization-hierarchy-feature';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useEntities } from '@/hooks/use-entities';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';
import { Character as DomainCharacter, Organization as DomainOrganization } from '@/lib/types';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';

export const Route = createFileRoute('/organizations/edit/$id')({
  component: EditOrganizationPage,
});

function EditOrganizationForm() {
  const { id: organizationId } = Route.useParams();
  const navigate = useNavigate();
  const { novel, updateEntityItem, saveItem, deleteItem } = useNovelRepository();
  const novelId = novel?.id;
  const { toast } = useToast();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();
  const { hasFeature } = useSubscription();
  const isOrganizationHierarchyEnabled = hasFeature(ORGANIZATION_HIERARCHY_FEATURE_KEY);

  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    organization: organizationData,
    sections: organizationSettings,
    isLoading: isLoadingFetching,
    updateOrganization: updateOrganizationLayer,
    deleteOrganization: deleteOrganizationLayer,
  } = useOrganizationAccessLayer(organizationId);

  const { processImages, isProcessing: isProcessingImages } = useImageProcessor();
  const { data: characterDocs, isLoading: isLoadingCharacters } = useEntities<DomainCharacter>('characters', isOrganizationHierarchyEnabled);
  const { data: organizationDocs, isLoading: isLoadingOrganizations } = useEntities<DomainOrganization>('organizations', isOrganizationHierarchyEnabled);
  const [isSaving, setIsSaving] = useState(false);
  const isProcessing = isProcessingImages;
  const [isDeleting, setIsDeleting] = useState(false);
  const isLoadingHierarchyRecords = isOrganizationHierarchyEnabled && (isLoadingCharacters || isLoadingOrganizations);

  const methods = useForm<OrganizationFormValues>({
    resolver: zodResolver(OrganizationFormSchema),
    defaultValues: {
      name: '',
      type: '',
      shortDescription: '',
      visualDescription: '',
      images: [],
      primaryImageId: null,
      pendingFiles: [],
      parentOrganizationIds: [],
      childOrganizationIds: [],
      childOrganizationTitles: {},
      organizationMemberGraphRelationships: [],
      organizationTitles: [],
      organizationMembers: [],
    },
  });

  const [lastResetId, setLastResetId] = useState<string | null>(null);

  // Sync images and primary index from background updates
  useEffect(() => {
    if (organizationData?.images && JSON.stringify(organizationData.images) !== JSON.stringify(methods.getValues('images'))) {
      methods.setValue('images', organizationData.images as OrganizationFormValues['images'], { shouldDirty: false });
    }
    if (organizationData?.primaryImageId !== undefined && organizationData.primaryImageId !== methods.getValues('primaryImageId')) {
      methods.setValue('primaryImageId', organizationData.primaryImageId, { shouldDirty: false });
    }
  }, [organizationData?.images, organizationData?.primaryImageId, methods]);

  useEffect(() => {
    if (organizationData && !isLoadingFetching && lastResetId !== organizationId) {
      methods.reset({
        ...organizationData,
        pendingFiles: [],
      });
      setLastResetId(organizationId);
      setIsLoading(false);
    } else if (!isLoadingFetching) {
      setIsLoading(false);
    }
  }, [organizationData, isLoadingFetching, organizationId, methods, lastResetId]);

  const onSubmit = async (values: OrganizationFormValues) => {
    if (!novelId || !organizationId) return;

    setIsSaving(true);
    try {
      const { pendingFiles, ...submittedOrganizationData } = values;
      const { defined: coreData, details } = splitBySchemaShape(OrganizationFormSchema, submittedOrganizationData);
      const organizationCoreData = isOrganizationHierarchyEnabled
        ? coreData
        : stripOrganizationHierarchyFields(coreData);

      let imagesUpdatedArray = [...(coreData.images || [])];

      // 1. Process images via shared hook
      if (pendingFiles && pendingFiles.length > 0) {
        const processedResults = await processImages(
          pendingFiles,
          novelId,
          `organizations/${organizationId}`,
          novel.ownerId
        );
        imagesUpdatedArray = [...imagesUpdatedArray, ...processedResults];
      }

      const primaryImageId = coreData.primaryImageId ?? imagesUpdatedArray[0]?.id ?? null;
      const mainImage = imagesUpdatedArray.find(image => image.id === primaryImageId);
      const mainImageUrl = mainImage?.profile?.url || mainImage?.original?.url || '';
      const mainImagePath = mainImage?.profile?.path || mainImage?.original?.path || '';

      // 2. Update via Access Layer
      await updateOrganizationLayer(organizationId, {
        ...organizationCoreData,
        id: organizationId,
        details,
        images: imagesUpdatedArray,
        primaryImageId,
        mainImageUrl,
        mainImagePath,
      });

      if (isOrganizationHierarchyEnabled) {
        if (!characterDocs || !organizationDocs) {
          throw new Error(t('entity_form.common.related_records_loading_error'));
        }

        await syncCharactersForOrganization({
          organizationId,
          previousCharacterIds: getOrganizationMemberIds(organizationData),
          nextCharacterIds: getOrganizationMemberIds(submittedOrganizationData),
          characters: characterDocs,
          updateEntityItem,
          saveRelationship: (relationship) => saveItem('entities', 'relationships', relationship),
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });

        await syncParentOrganizationsForOrganization({
          organizationId,
          previousParentOrganizationIds: getParentOrganizationIds(organizationData),
          nextParentOrganizationIds: getParentOrganizationIds(submittedOrganizationData),
          organizations: organizationDocs,
          updateEntityItem,
          saveRelationship: (relationship) => saveItem('entities', 'relationships', relationship),
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });

        await syncChildOrganizationsForOrganization({
          organizationId,
          previousChildOrganizationIds: getChildOrganizationIds(organizationData),
          nextChildOrganizationIds: getChildOrganizationIds(submittedOrganizationData),
          organizations: organizationDocs,
          updateEntityItem,
          saveRelationship: (relationship) => saveItem('entities', 'relationships', relationship),
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
      }

      toast({
        title: t('entity_form.organizations.update_success_title'),
        description: t('entity_form.organizations.update_success_desc', { name: values.name }),
      });
      navigate({ to: '/organizations' });
    } catch (error) {
      console.error('Update error:', error);
      toast({
        title: t('entity_form.common.update_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!organizationId) return;
    setIsDeleting(true);
    try {
      if (isOrganizationHierarchyEnabled) {
        if (!characterDocs || !organizationDocs) {
          throw new Error(t('entity_form.common.related_records_loading_error'));
        }

        await removeOrganizationFromCharacters({
          organizationId,
          characters: characterDocs,
          updateEntityItem,
          organizationMembers: organizationData?.organizationMembers || [],
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
        await removeOrganizationFromOrganizations({
          organizationId,
          organizations: organizationDocs,
          updateEntityItem,
          parentOrganizationIds: organizationData?.parentOrganizationIds || [],
          childOrganizationIds: organizationData?.childOrganizationIds || [],
          deleteRelationship: (relationshipId) => deleteItem('entities', 'relationships', relationshipId),
        });
      }
      await deleteOrganizationLayer(organizationId);
      toast({
        title: t('entity_form.organizations.delete_success_title'),
        description: t('entity_form.organizations.delete_success_desc'),
      });
      navigate({ to: '/organizations' });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('entity_form.common.delete_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
      setIsDeleting(false);
    }
  };

  const onInvalid = (errors: FieldErrors<OrganizationFormValues>) => {
    console.warn('Validation errors:', errors);
    toast({
      title: t('entity_form.common.validation_failed_title'),
      description: t('entity_form.common.validation_failed_generic_desc'),
      variant: 'destructive',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
        <CircularProgress color="primary" thickness={2} size={48} />
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.2em' }}>
          {t('entity_form.organizations.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
        <Page
          title={methods.watch('name') ? t('entity_form.organizations.edit_dynamic_title', { name: methods.watch('name') }) : t('entity_form.organizations.edit_title')}
          description={t('entity_form.organizations.edit_desc')}
          breadcrumbs={[
            { label: t('entity_list.organizations.title'), href: '/organizations' },
            { label: (methods.watch('name') as string) || t('entity_form.organizations.breadcrumb_faction'), href: `/organizations/view/${organizationId}` },
            { label: t('entity_form.common.edit') }
          ]}
          actions={
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="error"
                disabled={isSaving || isDeleting || isLoadingHierarchyRecords}
                onClick={() => setShowDeleteConfirm(true)}
                startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Trash2 sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: 2 }}
              >
                {t('entity_form.common.delete')}
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={isSaving || isDeleting || isLoadingHierarchyRecords}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {t('entity_form.organizations.update')}
              </Button>
            </Stack>
          }
        >

          <DeleteConfirmationModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            isDeleting={isDeleting}
            itemName={organizationData?.name}
            description={t('entity_form.organizations.delete_desc')}
          />

          <Grid container spacing={4}>
            <Grid size={resolveResponsive({ xs: 12, lg: 8 })}>
              <Stack spacing={4}>
                <BasicIdentitySection />
                {isOrganizationHierarchyEnabled && (
                  <MembershipSection
                    organizationId={organizationId}
                    enableRelationshipEditing
                    isFormSubmitting={isSaving}
                  />
                )}
                {novelId && <CustomFieldsSection novelId={novelId} sections={organizationSettings} />}
              </Stack>
            </Grid>

            <Grid size={resolveResponsive({ xs: 12, lg: 4 })}>
              <Box component="aside" sx={{ position: { lg: 'sticky' }, top: 24 }}>
                <ImageManager
                  onUpdate={(updates) => {
                    (Object.keys(updates) as Array<keyof typeof updates>).forEach((key) => {
                      const value = updates[key];
                      if (value !== undefined) {
                        methods.setValue(key as Path<OrganizationFormValues>, value, { shouldDirty: true, shouldTouch: true });
                      }
                    });
                  }}
                  organization={{ ...methods.getValues(), id: organizationId } as DomainOrganization}
                  pendingFiles={(methods.watch('pendingFiles') as File[]) || []}
                  isProcessing={isProcessing}
                  visualDescription={methods.watch('visualDescription')}
                  onVisualDescriptionChange={(value) => methods.setValue('visualDescription', value, { shouldDirty: true })}
                />
              </Box>
            </Grid>
          </Grid>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isDeleting || isLoadingHierarchyRecords}
          label={t('entity_form.organizations.update')}
        />
      </form>
    </FormProvider>
  );
}

export default function EditOrganizationPage() {
  const { t } = useTranslation();
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
          <CircularProgress color="primary" thickness={2} size={48} />
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.2em' }}>
            {t('entity_form.organizations.materializing')}
          </Typography>
        </Box>
      }>
        <EditOrganizationForm />
      </Suspense>
    </AuthGuard>
  );
}
